import _ from 'lodash';
import {
  isZurichProduct,
  type AccountDto,
  type PrimeXProductType,
} from '../../../../../../types';
import type { Nullable } from '../../../../../../types/common/utility';
import type { FirmographicDataDto } from '../../../../../../types/firmo/firmographic-data.dto';
import type { RaterXPreset, RiskData } from '../../api/types/rater-x-preset';
import type { PrimeXQuoteState } from '../../types';
import {
  ReferralStrategyForEdit,
  type UnderwriteQuoteState,
} from '../../views/underwriter/types';
import { buildComponentDynamicFields } from '../../hooks/useAddQuoteToCart';
import { _toRecordFilteredByRoot } from '../api';
import type { RaterResponse } from '../../../../../../api/quotes.api.types';
import type { CoverageOptionDto } from '../../../../../../api/submissions/submission-coverages.types';

type BasePayloadBuilderParams = {
  productType: PrimeXProductType;
  createdDateTime?: string;
  effectiveDate: string;
  endDate?: string;
  isRenewal?: boolean;
  fieldValues?: Record<string, any>[];
  account?: (AccountDto | FirmographicDataDto) & { foreignRevenue?: number };
  risk?: Partial<RiskData> | undefined;
  policyValue?: RaterXPreset['global']['value'];
  commissionRate: number | undefined;
  brokerFee: number | undefined;
  quoteCommissionRate?: number | undefined;
  isAgencyBilled: boolean | undefined;
  agencyName: string | undefined;
  marketType: string | undefined;
  agencyId: string | undefined;
  accountId: string | undefined;
  components?: RaterXPreset['components'];
  coverageOption?: CoverageOptionDto;
  carrierPrefix?: string;
  treatyId?: string;
};

export function baseRaterXPresetPayloadBuilder(
  params: BasePayloadBuilderParams
) {
  const coverageOptionMap = buildCoverageOptionMap(params.coverageOption);
  return {
    effectiveDate: params.effectiveDate,
    endDate: params.endDate,
    isRenewal: params.isRenewal ?? false,
    fieldValues: params.fieldValues,
    ...(params.carrierPrefix && { carrierPrefix: params.carrierPrefix }),
    ...(params.treatyId && { treatyId: params.treatyId }),
    firmoData: firmoBuilder(params.account, params?.accountId),
    ...(params.risk && { riskData: params.risk }),
    ...(typeof params.quoteCommissionRate === 'number' && {
      commissionRate: params.quoteCommissionRate,
    }),
    ...agencyDataBuilder(params),
    ...(params.policyValue && { policyValue: params.policyValue }),
    ...(params.coverageOption &&
      !isZurichProduct(params.productType) && {
        policyValue: buildPolicyValues(
          params.productType,
          params.coverageOption
        ),
      }),
    ...(params.components &&
      !_.isEmpty(params.components) && {
        components: buildComponentDynamicFields(
          params.components,
          coverageOptionMap
        ),
      }),
    ...(params.coverageOption &&
      _.isEmpty(params.components) && {
        components: buildComponentFieldsFromCoverageOption(coverageOptionMap),
      }),
  };
}

function buildPolicyValues(
  productType: PrimeXProductType,
  coverageOption: CoverageOptionDto | undefined
) {
  if (!coverageOption || isZurichProduct(productType)) {
    return undefined;
  }

  const coverage = coverageOption?.coverages[0];

  return {
    props: {
      limit: {
        value: coverage?.limit.value,
      },
      ...(coverage?.retention.value && {
        deductible: {
          value: coverage?.retention.value,
        },
      }),
    },
  };
}

function agencyDataBuilder(params: Partial<BasePayloadBuilderParams>) {
  return {
    agencyData: {
      commissionRate: params.commissionRate,
      isAgencyBilled: params.isAgencyBilled,
      agencyName: params.agencyName,
      agencyId: params.agencyId,
      marketType: params.marketType,
      brokerFee: params.brokerFee,
    },
  };
}

export function firmoBuilder(
  account: BasePayloadBuilderParams['account'],
  accountId: string | undefined
) {
  if (!account) {
    return undefined;
  }

  return {
    classificationCode: account.industryCode,
    countryAbbreviation: account.country,
    stateAbbreviation: account.state || account.country,
    totalRevenue: account.totalRevenue,
    revenue: account.revenue,
    otherRevenue: account.otherRevenue ?? account.foreignRevenue,
    numberOfPersonalRecords: Number(account.numberOfPersonalRecords),
    city: account.city,
    additionalNamedInsureds: account.additionalNamedInsured ?? [],
    accountId,
    ownershipType: account.ownershipType,
    postalCode: account.zipCode,
    yearsInBusiness: account.yearEstablished
      ? new Date().getFullYear() - account.yearEstablished
      : 0,
    isFranchise: account.isFranchise ?? false,
    hasConnectivityToFranchisorOrFranchise:
      account.franchiseSystemConnectivity ?? false,
    numberOfEmployees: account.noOfEmployeesAll,
  };
}

export type RaterXPresetPayloadBuilderParams = {
  policyValue?: RaterXPreset['global']['value'];
  coverages?: PrimeXQuoteState['coverageState'];
  ratingOptions?: UnderwriteQuoteState['ratingOptions'];
  strictMode?: boolean;
  isUwView?: boolean;
  productVersion?: number;
  raterMajorVersion?: number;
  raterMinorVersion?: number;
  createdDateTime?: string;
  categoryCustomizations?: UnderwriteQuoteState['categoryCustomizations'];
  componentGlobals?: PrimeXQuoteState['componentGlobals'];
  quoteComponentsRaterResponse: Nullable<Record<string, any>>;
} & BasePayloadBuilderParams;

export function raterXRecalculatePayloadBuilder(
  params: RaterXPresetPayloadBuilderParams
) {
  return {
    ...baseRaterXPresetPayloadBuilder(params),
    strictMode: params.strictMode,
    policyValue: params.policyValue,
    fieldValues: generateFilteredFieldValues(
      params.fieldValues,
      params.coverages,
      params.ratingOptions,
      params.componentGlobals
    ),
    ...(params.componentGlobals &&
      !_.isEmpty(params.componentGlobals) && {
        components: generateComponentRequest(
          params.coverages,
          params.ratingOptions,
          params.componentGlobals,
          params.quoteComponentsRaterResponse
        ),
      }),
    ...(params.productVersion && { productVersion: params.productVersion }),
    ...(params.raterMajorVersion != null && {
      raterMajorVersion: params.raterMajorVersion,
    }),
    ...(params.raterMinorVersion != null && {
      raterMinorVersion: params.raterMinorVersion,
    }),
    ...(params.createdDateTime && { createdDateTime: params.createdDateTime }),
    ...(params.categoryCustomizations && {
      ...params.categoryCustomizations,
    }),
    ...(params.isUwView !== undefined && { isUw: params.isUwView }),
  };
}

export function generateFilteredFieldValues(
  fieldValues: BasePayloadBuilderParams['fieldValues'],
  coverages: PrimeXQuoteState['coverageState'] | undefined,
  ratingOptions: UnderwriteQuoteState['ratingOptions'] | undefined,
  componentGlobals: PrimeXQuoteState['componentGlobals']
) {
  if (!coverages && !ratingOptions) {
    return (fieldValues || []).map((item) => ({
      ...item,
      isSelected: item.isSelected ?? true,
    }));
  }

  if (!coverages) {
    return generateFieldValues(fieldValues, undefined, ratingOptions);
  }

  if (!ratingOptions) {
    return generateFieldValues(fieldValues, coverages, undefined);
  }

  const componentTypes = new Set(Object.keys(componentGlobals || {}));
  const rootCoverages = _toRecordFilteredByRoot(componentTypes, coverages);
  const rootRatingOptions = _toRecordFilteredByRoot(
    componentTypes,
    ratingOptions
  );

  return generateFieldValues(fieldValues, rootCoverages, rootRatingOptions);
}

export function generateFieldValues(
  fieldValues: BasePayloadBuilderParams['fieldValues'],
  coverages: PrimeXQuoteState['coverageState'] | undefined,
  ratingOptions: UnderwriteQuoteState['ratingOptions'] | undefined
) {
  if (!coverages && !ratingOptions) {
    return (fieldValues || []).map((item) => ({
      ...item,
      isSelected: item.isSelected ?? true,
    }));
  }

  const coverageIds = Object.values(coverages || {}).map(
    ({ field }: any) => field?.id
  );
  const ratingOptionsIds = Object.values(ratingOptions || {}).map(
    ({ field }: any) => field?.id
  );

  const mergedIds = [...coverageIds, ...ratingOptionsIds];

  const fieldValuesMap = (fieldValues || []).reduce((acc, item) => {
    if (mergedIds.includes(item.fieldId as string)) {
      return acc;
    }
    acc[item.fieldId as string] = {
      ...item,
      isSelected: item.isSelected ?? true,
    };
    return acc;
  }, {});

  Object.values(coverages).forEach((coverage) => {
    if (coverage.field) {
      fieldValuesMap[coverage.field.id] = {
        fieldId: coverage.field.id,
        value: coverage.value,
        isPegValue: coverage.isPegValue,
        referralStrategyForEdit:
          coverage.referralStrategyForEdit || ReferralStrategyForEdit.NONE,
        isSelected: coverage.isSelected ?? false,
        ...(coverage.isDirty && { isDirty: coverage.isDirty }),
      };
    }
  });

  if (ratingOptions) {
    Object.values(ratingOptions).forEach((option) => {
      if (option.field) {
        fieldValuesMap[option.field.id] = {
          fieldId: option.field.id,
          isPegValue: option.isPegValue,
          referralStrategyForEdit:
            option.referralStrategyForEdit || ReferralStrategyForEdit.NONE,
          isSelected: option.isSelected ?? false,
          ...(!option.readOnly && { value: option.value }),
        };
      }
    });
  }

  return Object.values(fieldValuesMap);
}

export function generateComponentRequest(
  coverages: Nullable<PrimeXQuoteState['coverageState']>,
  ratingOptions: Nullable<UnderwriteQuoteState['ratingOptions']>,
  componentGlobals: Nullable<PrimeXQuoteState['componentGlobals']>,
  quoteComponentsRaterResponse: Nullable<Record<string, RaterResponse>>
) {
  const groupedCoverages: Record<string, any> = _.groupBy(
    coverages,
    'productType'
  );
  const groupedRatingOptions: Record<string, any> = _.groupBy(
    ratingOptions,
    'productType'
  );

  return Object.entries(componentGlobals || {}).reduce((acc, [key, value]) => {
    /*
     -- FIXME : This is a temporary fix for the ZSP_CRIME to recalculate the deductible.
     -- It should be removed once the backend is fixed.
     */
    let _policyValues = value.value.props;
    if (!value.lockValues?.includes('deductible') && key === 'ZSP_CRIME') {
      _policyValues = _.omit(value.value.props, ['deductible']);
    }

    return {
      ...acc,
      [key]: {
        policyValue: {
          props: _policyValues,
        },
        ...(quoteComponentsRaterResponse && {
          productVersion:
            quoteComponentsRaterResponse[key]?.raterResponseFlags
              .productVersion,
        }),
        fieldValues: generateFieldValues(
          [],
          groupedCoverages[key],
          groupedRatingOptions[key]
        ),
      },
    };
  }, {});
}

export function buildCoverageOptionMap(
  coverageOption: Nullable<CoverageOptionDto>
) {
  const coverageOptionMap: Record<
    string,
    { limit: number; deductible?: number }
  > = {};

  if (coverageOption?.coverages) {
    coverageOption.coverages.forEach((coverage) => {
      if (coverage.part.value) {
        const componentType =
          COVERAGE_PART_TO_COMPONENT_MAP[coverage.part.value];
        if (componentType && coverage.limit.value != null) {
          coverageOptionMap[componentType] = {
            limit: coverage.limit.value,
            ...(coverage.retention.value != null && {
              deductible: coverage.retention.value,
            }),
          };
        }
      }
    });
  }

  return coverageOptionMap;
}

export function buildComponentFieldsFromCoverageOption(
  coverageOptionMap: Record<string, { limit: number; deductible?: number }>
) {
  return Object.keys(coverageOptionMap).reduce((acc, key) => {
    const coverage = coverageOptionMap[key];
    return {
      ...acc,
      [key]: {
        policyValue: {
          props: {
            limit: { value: coverage.limit },
            ...(coverage.deductible && {
              deductible: { value: coverage.deductible },
            }),
          },
        },
      },
    };
  }, {});
}

export const COVERAGE_PART_TO_COMPONENT_MAP: Record<string, string> = {
  'Directors and Officers': 'ZSP_DNO',
  'Employment Practices Liability': 'ZSP_EPL',
  Crime: 'ZSP_CRIME',
  Fiduciary: 'ZSP_FIDUCIARY',
};

export const COVERAGE_PART_TO_PRODUCT_LINE_MAP: Record<string, string> = {
  'Directors and Officers': 'DNO',
  'Employment Practices Liability': 'EPL',
  Crime: 'CRIME',
  Fiduciary: 'FIDUCIARY',
};
